import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { campaignsAPI } from '../utils/api';

const HintBox = ({ show, children }) => (
  <div style={{
    overflow: 'hidden',
    maxHeight: show ? 200 : 0,
    opacity: show ? 1 : 0,
    transition: 'max-height 0.3s ease, opacity 0.25s ease',
    marginBottom: show ? 8 : 0,
  }}>
    <div style={{
      background: 'var(--bg-secondary)',
      border: '1px solid var(--border)',
      borderRadius: 8,
      padding: '10px 12px',
      fontSize: 12,
      color: 'var(--text-muted)',
      lineHeight: 1.6,
      marginTop: 6,
    }}>
      {children}
    </div>
  </div>
);

const HintButton = ({ active, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    style={{
      width: 18, height: 18, borderRadius: '50%',
      background: active ? 'var(--accent-blue)' : 'var(--bg-secondary)',
      border: '1px solid ' + (active ? 'var(--accent-blue)' : 'var(--border)'),
      color: active ? '#fff' : 'var(--text-muted)',
      fontSize: 10, fontWeight: 700, cursor: 'pointer',
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      transition: 'all 0.2s', flexShrink: 0,
    }}
  >?</button>
);

export default function Campaigns({ onSelect, onCreated }) {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: '', domain: '', platform: 'google', cpc: '', google_ads_customer_id: '' });
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState(null);
  const [limitError, setLimitError] = useState('');
  const [editing, setEditing] = useState(null); // campaign id being edited
  const [editForm, setEditForm] = useState({});
  const [showCidHint, setShowCidHint] = useState(false);
  const [showCpcHint, setShowCpcHint] = useState(false);
  const [platform, setPlatform] = useState('wordpress');

  useEffect(() => {
    campaignsAPI.list()
      .then(setCampaigns)
      .catch(() => setCampaigns([]))
      .finally(() => setLoading(false));
  }, []);

  const isValidCustomerId = (val) => /^\d{10}$/.test(val.replace(/-/g, ''));

  const handleCreate = async () => {
    if (!form.name.trim() || !form.domain.trim()) return alert('Name and domain are required.');
    if (!form.google_ads_customer_id.trim()) return alert('Google Ads Customer ID is required.');
    if (!isValidCustomerId(form.google_ads_customer_id)) return alert('Google Ads Customer ID must be 10 digits (e.g. 733-062-7313).');
    setCreating(true);
    try {
      const result = await campaignsAPI.create({
        name: form.name.trim(),
        domain: form.domain.trim(),
        platform: form.platform,
        cpc: form.cpc ? parseFloat(form.cpc) : 0,
        google_ads_customer_id: form.google_ads_customer_id.replace(/-/g, '').trim() || null,
      });
      setCreated(result);
      setCampaigns(prev => [result, ...prev]);
      setShowCreate(false);
      setLimitError('');
      setForm({ name: '', domain: '', platform: 'google', cpc: '', google_ads_customer_id: '' });
      if (onCreated) onCreated(result);
    } catch (e) {
      const msg = e?.response?.data?.detail || '';
      if (e?.response?.status === 403 && msg.includes('Campaign limit')) {
        setShowCreate(false);
        setLimitError(msg);
      } else {
        alert('Error creating campaign. Is the backend running?');
      }
    } finally {
      setCreating(false);
    }
  };

  const handleSelect = (id) => { if (onSelect) onSelect(id); navigate('/dashboard'); };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete campaign "${name}"? This cannot be undone.`)) return;
    try {
      await campaignsAPI.delete(id);
      setCampaigns(prev => prev.filter(c => c.id !== id));
    } catch {
      alert('Failed to delete campaign.');
    }
  };

  const startEdit = (c) => {
    setEditing(c.id);
    setEditForm({
      name: c.name || '',
      cpc: c.cpc || '',
      google_ads_customer_id: c.google_ads_customer_id || '',
    });
  };

  const handleShowScript = async (c) => {
    try {
      const result = await campaignsAPI.getTrackingScript(c.id);
      setCreated({ ...c, tracking_script: result.tracking_script });
    } catch {
      alert('Could not fetch tracking script.');
    }
  };

  const handleSaveEdit = async (id) => {
    try {
      await campaignsAPI.update(id, {
        name: editForm.name.trim() || undefined,
        cpc: editForm.cpc ? parseFloat(editForm.cpc) : undefined,
        google_ads_customer_id: editForm.google_ads_customer_id.replace(/-/g, '').trim() || undefined,
      });
      setCampaigns(prev => prev.map(c => c.id === id ? {
        ...c,
        name: editForm.name.trim() || c.name,
        cpc: editForm.cpc ? parseFloat(editForm.cpc) : c.cpc,
        google_ads_customer_id: editForm.google_ads_customer_id.replace(/-/g, '').trim() || c.google_ads_customer_id,
      } : c));
      setEditing(null);
    } catch {
      alert('Failed to save changes.');
    }
  };

  if (loading) return <div className="loading"><div className="spinner" />Loading...</div>;

  return (
    <div>
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div><h1>Campaigns</h1><p>Manage your protected ad campaigns</p></div>
        <button className="btn btn-primary" onClick={() => setShowCreate(true)}>+ New Campaign</button>
      </div>

      {/* Campaign limit banner */}
      {limitError && (
        <div style={{ marginBottom: 12, padding: '14px 18px', borderRadius: 10, background: 'rgba(248,81,73,0.08)', border: '1px solid rgba(248,81,73,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 18 }}>🔒</span>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--accent-red)', marginBottom: 2 }}>Campaign limit reached</div>
              <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{limitError}</div>
            </div>
          </div>
          <a href="/settings?tab=billing" style={{ flexShrink: 0, padding: '8px 18px', borderRadius: 8, background: 'var(--accent-blue)', color: '#fff', fontWeight: 600, fontSize: 13, textDecoration: 'none' }}>
            Upgrade Plan →
          </a>
        </div>
      )}

      {/* Create modal */}
      {showCreate && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200 }}>
          <div className="card" style={{ width: 480 }}>
            <h2 style={{ marginBottom: 12 }}>New Campaign</h2>
            <div className="form-group">
              <label>Campaign Name</label>
              <input placeholder="e.g. Google Ads - Brand" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
            </div>
            <div className="form-group">
              <label>Website Domain</label>
              <input placeholder="yoursite.com" value={form.domain} onChange={e => setForm(f => ({ ...f, domain: e.target.value }))} />
            </div>
            <div className="form-group">
              <label>Platform</label>
              <select value={form.platform} onChange={e => setForm(f => ({ ...f, platform: e.target.value }))}>
                <option value="google">Google Ads</option>
              </select>
            </div>
            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                Google Ads Customer ID <span style={{ color: '#dc2626', fontWeight: 600 }}>*</span>
                <HintButton active={showCidHint} onClick={() => setShowCidHint(v => !v)} />
              </label>
              <HintBox show={showCidHint}>
                📍 Sign in to <a href="https://ads.google.com" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent-blue)', textDecoration: 'none', fontWeight: 500 }}>ads.google.com</a> — your Customer ID appears in the <strong>top-right corner</strong> below your email address. It looks like <code style={{ background: 'var(--bg-primary)', padding: '1px 5px', borderRadius: 4 }}>123-456-7890</code>.<br />
                You can enter it with or without dashes — we'll clean it up automatically.
              </HintBox>
              {(() => {
                const val = form.google_ads_customer_id;
                const digits = val.replace(/-/g, '');
                const hasInput = val.trim().length > 0;
                const valid = /^\d{10}$/.test(digits);
                const tooLong = digits.length > 10;
                const hasNonDigit = /[^0-9\-]/.test(val);
                return (
                  <>
                    <input
                      placeholder="e.g. 733-062-7313"
                      value={val}
                      onChange={e => setForm(f => ({ ...f, google_ads_customer_id: e.target.value }))}
                      style={{
                        borderColor: hasInput ? (valid ? '#22c55e' : '#ef4444') : undefined,
                        boxShadow: hasInput ? `0 0 0 3px ${valid ? 'rgba(34,197,94,0.1)' : 'rgba(239,68,68,0.1)'}` : undefined,
                      }}
                    />
                    {hasInput && (
                      <div style={{ fontSize: 11, marginTop: 4, display: 'flex', alignItems: 'center', gap: 4,
                        color: valid ? '#15803d' : '#dc2626' }}>
                        {valid ? (
                          <><span>✓</span> Valid Customer ID</>
                        ) : hasNonDigit ? (
                          <><span>✕</span> Only digits and dashes are allowed</>
                        ) : tooLong ? (
                          <><span>✕</span> Too many digits — Customer ID is exactly 10 digits</>
                        ) : (
                          <><span>✕</span> {digits.length}/10 digits entered</>
                        )}
                      </div>
                    )}
                    {!hasInput && (
                      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                        Each campaign can link to a different Google Ads account
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                Cost Per Click (CPC) <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>— used to calculate savings</span>
                <HintButton active={showCpcHint} onClick={() => setShowCpcHint(v => !v)} />
              </label>
              <HintBox show={showCpcHint}>
                💡 <strong>Not sure? That's fine</strong> — you can skip this for now and update it anytime from campaign settings.<br /><br />
                If you want an estimate: the Google Ads average CPC is <strong>$1–$3</strong> for most industries, but competitive niches like legal or finance can be <strong>$10–$50+</strong>. Check your <strong>Google Ads → Campaigns → Avg. CPC column</strong> once you have some data.
              </HintBox>
              <input type="number" min="0" step="0.01" placeholder="e.g. 2.50" value={form.cpc} onChange={e => setForm(f => ({ ...f, cpc: e.target.value }))} />
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button className="btn btn-primary" onClick={handleCreate} disabled={creating} style={{ flex: 1 }}>
                {creating ? 'Creating...' : 'Create Campaign'}
              </button>
              <button className="btn btn-ghost" onClick={() => setShowCreate(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Tracking script modal */}
      {created && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200, overflowY: 'auto', padding: '24px 0' }}>
          <div className="card" style={{ width: 660, margin: 'auto' }}>
            <h2 style={{ marginBottom: 4 }}>Install Tracking Script</h2>
            <p style={{ color: 'var(--text-secondary)', marginBottom: 12, fontSize: 13 }}>
              Copy the script below and follow the guide for your platform.
            </p>

            {/* Script block */}
            <div style={{
              background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 8,
              padding: 16, fontFamily: 'var(--font-body)', fontVariantNumeric: 'tabular-nums', fontSize: 12, color: 'var(--accent-green)',
              whiteSpace: 'pre', overflowX: 'auto', maxHeight: 160, overflowY: 'auto',
            }}>
              {created.tracking_script}
            </div>

            {/* Platform guide */}
            <div style={{ marginTop: 16, border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>
              {/* Tab bar */}
              <div style={{ display: 'flex', borderBottom: '1px solid var(--border)', background: 'var(--bg-secondary)' }}>
                {[
                  { id: 'wordpress', label: '🔵 WordPress' },
                  { id: 'shopify',   label: '🟢 Shopify' },
                  { id: 'webflow',   label: '🟣 Webflow' },
                  { id: 'wix',       label: '🟡 Wix' },
                  { id: 'html',      label: '🔧 Custom HTML' },
                ].map(p => (
                  <button key={p.id} type="button" onClick={() => setPlatform(p.id)} style={{
                    flex: 1, padding: '8px 4px', fontSize: 11, fontWeight: platform === p.id ? 700 : 400,
                    border: 'none', borderBottom: platform === p.id ? '2px solid var(--accent-blue)' : '2px solid transparent',
                    background: 'none', cursor: 'pointer', color: platform === p.id ? 'var(--accent-blue)' : 'var(--text-muted)',
                    fontFamily: 'inherit', transition: 'all 0.15s',
                  }}>{p.label}</button>
                ))}
              </div>

              {/* Tab content */}
              <div style={{ padding: '14px 16px', fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.7 }}>
                {platform === 'wordpress' && (
                  <ol style={{ paddingLeft: 18, margin: 0 }}>
                    <li>In your WordPress dashboard go to <strong>Appearance → Theme File Editor</strong> (or install the <em>Insert Headers and Footers</em> plugin).</li>
                    <li>Open <strong>header.php</strong> and find the closing <code>&lt;/body&gt;</code> tag — OR paste it in the <strong>Footer Scripts</strong> field of the plugin.</li>
                    <li>Paste the copied script just before <code>&lt;/body&gt;</code>.</li>
                    <li>Click <strong>Update File</strong> / <strong>Save</strong>.</li>
                    <li>If you use a caching plugin (WP Rocket, W3 Total Cache, etc.) <strong>purge your cache</strong> after saving.</li>
                  </ol>
                )}
                {platform === 'shopify' && (
                  <ol style={{ paddingLeft: 18, margin: 0 }}>
                    <li>In your Shopify admin go to <strong>Online Store → Themes</strong>.</li>
                    <li>Click <strong>Actions → Edit Code</strong> on your active theme.</li>
                    <li>Open <strong>Layout → theme.liquid</strong>.</li>
                    <li>Find <code>&lt;/body&gt;</code> near the bottom and paste the script just before it.</li>
                    <li>Click <strong>Save</strong>. No cache clearing needed on Shopify.</li>
                  </ol>
                )}
                {platform === 'webflow' && (
                  <ol style={{ paddingLeft: 18, margin: 0 }}>
                    <li>Open your Webflow project and go to <strong>Project Settings → Custom Code</strong>.</li>
                    <li>Paste the script in the <strong>Footer Code</strong> box.</li>
                    <li>Click <strong>Save Changes</strong>, then <strong>Publish</strong> your site.</li>
                    <li>The script will now run on every page of your Webflow site.</li>
                  </ol>
                )}
                {platform === 'wix' && (
                  <ol style={{ paddingLeft: 18, margin: 0 }}>
                    <li>In Wix, go to <strong>Settings → Custom Code</strong> (requires Wix Premium).</li>
                    <li>Click <strong>+ Add Custom Code</strong>.</li>
                    <li>Paste the script, set placement to <strong>Body — End</strong>, apply to <strong>All Pages</strong>.</li>
                    <li>Click <strong>Apply</strong> and publish your site.</li>
                  </ol>
                )}
                {platform === 'html' && (
                  <ol style={{ paddingLeft: 18, margin: 0 }}>
                    <li>Open your HTML file in a code editor.</li>
                    <li>Find the closing <code>&lt;/body&gt;</code> tag.</li>
                    <li>Paste the script directly above it.</li>
                    <li>Save and upload/deploy the file to your server.</li>
                    <li>If using a CDN (Cloudflare, Fastly), <strong>purge your cache</strong> after deploying.</li>
                  </ol>
                )}
              </div>
            </div>

            <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
              <button className="btn btn-primary" onClick={() => navigator.clipboard.writeText(created.tracking_script)}>Copy Script</button>
              <button className="btn btn-ghost" onClick={() => setCreated(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit modal */}
      {editing && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200 }}>
          <div className="card" style={{ width: 480 }}>
            <h2 style={{ marginBottom: 12 }}>Edit Campaign</h2>
            <div className="form-group">
              <label>Campaign Name</label>
              <input value={editForm.name} onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))} />
            </div>
            <div className="form-group">
              <label>Google Ads Customer ID</label>
              <input
                placeholder="e.g. 733-062-7313"
                value={editForm.google_ads_customer_id}
                onChange={e => setEditForm(f => ({ ...f, google_ads_customer_id: e.target.value }))}
              />
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                Link this campaign to a specific Google Ads account
              </div>
            </div>
            <div className="form-group">
              <label>CPC ($)</label>
              <input type="number" min="0" step="0.01" value={editForm.cpc} onChange={e => setEditForm(f => ({ ...f, cpc: e.target.value }))} />
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button className="btn btn-primary" onClick={() => handleSaveEdit(editing)} style={{ flex: 1 }}>Save Changes</button>
              <button className="btn btn-ghost" onClick={() => setEditing(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {campaigns.length === 0 ? (
        <div className="empty">
          <h3>No campaigns yet</h3>
          <p>Create your first campaign to start protecting your ads.</p>
          <button className="btn btn-primary" style={{ marginTop: 12 }} onClick={() => setShowCreate(true)}>+ Create Campaign</button>
        </div>
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr><th>Name</th><th>Domain</th><th>Google Ads ID</th><th>CPC</th><th>Status</th><th>Actions</th></tr>
            </thead>
            <tbody>
              {campaigns.map(c => (
                <tr key={c.id}>
                  <td style={{ fontWeight: 500 }}>{c.name}</td>
                  <td style={{ color: 'var(--text-secondary)', fontFamily: 'var(--font-body)', fontVariantNumeric: 'tabular-nums', fontSize: 12 }}>{c.domain}</td>
                  <td style={{ fontFamily: 'var(--font-body)', fontVariantNumeric: 'tabular-nums', fontSize: 12, color: c.google_ads_customer_id ? 'var(--accent-blue)' : 'var(--text-muted)' }}>
                    {c.google_ads_customer_id || '—'}
                  </td>
                  <td style={{ fontFamily: 'var(--font-body)', fontVariantNumeric: 'tabular-nums', fontSize: 13, color: 'var(--text-secondary)' }}>
                    {c.cpc ? `$${parseFloat(c.cpc).toFixed(2)}` : '—'}
                  </td>
                  <td><span className={`badge ${c.is_active ? 'clean' : 'warning'}`}>{c.is_active ? 'Active' : 'Paused'}</span></td>
                  <td>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button className="btn btn-ghost" style={{ padding: '6px 12px', fontSize: 12 }} onClick={() => handleSelect(c.id)}>Select</button>
                      <button className="btn btn-ghost" style={{ padding: '6px 12px', fontSize: 12 }} onClick={() => handleShowScript(c)}>Script</button>
                      <button className="btn btn-ghost" style={{ padding: '6px 12px', fontSize: 12 }} onClick={() => startEdit(c)}>Edit</button>
                      <button className="btn btn-ghost" style={{ padding: '6px 10px', fontSize: 12, color: 'var(--accent-red)', borderColor: 'rgba(248,81,73,0.3)' }} onClick={() => handleDelete(c.id, c.name)}>Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
